import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FY_MONTH_NAMES, formatMoney, type MonthFigure, type Row } from "./api";

/**
 * Every job code on a campus, twelve months of running position, one line
 * each, toggleable.
 *
 * **Every line gets its own visual identity, and none of them shares a hue
 * with another.** Eight validated hues are assigned in fixed order, and past
 * the eighth the identity is carried by hue *plus* line style — solid, then
 * dashed, then dotted. That is composite encoding, and it is the reason the
 * chart can show twenty-four distinct lines without inventing colours.
 *
 * Cycling hue alone would put two lines in the same blue, which reads as one
 * series measured twice; and past about eight, hues stop being separable at
 * all under colour-vision deficiency. The dash pattern also happens to be the
 * one cue that survives a monochrome print.
 *
 * Colour follows the row, not its rank among the selected: the slot is fixed
 * by the row's position in the roster before anything is toggled, so hiding
 * one line never repaints the others.
 */

const SERIES_LIGHT = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100",
  "#e87ba4", "#008300", "#4a3aa7", "#e34948",
];
const SERIES_DARK = [
  "#3987e5", "#d95926", "#199e70", "#c98500",
  "#d55181", "#008300", "#9085e9", "#e66767",
];
/** Solid, dashed, dotted — the second axis of identity past eight lines. */
const DASHES = [undefined, "7 4", "2 3"];

export interface ChartRow extends Row {
  months: MonthFigure[];
}

interface Style {
  readonly colour: string;
  readonly dash: string | undefined;
}

/** Follows the viewer's theme, including a change made while the page is open. */
function useDark(): boolean {
  const [dark, setDark] = useState(() => prefersDark());
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = (): void => setDark(prefersDark());
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return dark;
}

function prefersDark(): boolean {
  const chosen = document.documentElement.dataset["theme"];
  if (chosen === "dark") return true;
  if (chosen === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function CampusChart({ rows }: { rows: ChartRow[] }) {
  const dark = useDark();
  const palette = dark ? SERIES_DARK : SERIES_LIGHT;

  const plottable = useMemo(
    () =>
      rows.filter(
        (row) => row.months.filter((month) => month.position !== undefined).length > 1,
      ),
    [rows],
  );

  // Staff first: a campus's general, camps and fund buckets are real money but
  // nobody's balance, and opening on thirty lines shows nothing.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(plottable.filter((row) => row.isStaff).map((row) => row.rowId)),
  );

  const styles = useMemo(() => {
    const map = new Map<string, Style>();
    plottable.forEach((row, index) => {
      map.set(row.rowId, {
        colour: palette[index % palette.length]!,
        dash: DASHES[Math.floor(index / palette.length)] ?? "1 4",
      });
    });
    return map;
  }, [plottable, palette]);

  /**
   * Recharts wants one object per x-value with a key per series. Positions
   * become numbers here, which is geometry — the tooltip reads the original
   * decimal strings from `labels`, so no displayed figure is ever a float.
   */
  const { data, labels } = useMemo(() => {
    const monthCount = Math.max(
      ...plottable.map(
        (row) => row.months.filter((month) => month.position !== undefined).length,
      ),
      0,
    );
    const labelled = new Map<string, string>();
    const points = Array.from({ length: monthCount }, (_, index) => {
      const entry: Record<string, string | number> = { month: FY_MONTH_NAMES[index] ?? "" };
      for (const row of plottable) {
        const shown = row.months.filter((month) => month.position !== undefined);
        const value = shown[index]?.position;
        if (value !== undefined) {
          entry[row.rowId] = Number(value);
          labelled.set(`${row.rowId}@${index}`, value);
        }
      }
      return entry;
    });
    return { data: points, labels: labelled };
  }, [plottable]);

  if (plottable.length === 0) return null;
  const shown = plottable.filter((row) => selected.has(row.rowId));

  return (
    <div className="chart">
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={data} margin={{ top: 8, right: 56, bottom: 4, left: 8 }}>
          <CartesianGrid stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: "var(--text-muted)", fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: "var(--border)" }}
          />
          <YAxis
            tick={{ fill: "var(--text-muted)", fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={78}
            tickFormatter={(value: number) =>
              Math.abs(value) >= 1000
                ? `${value < 0 ? "−" : ""}$${Math.round(Math.abs(value) / 1000)}k`
                : formatMoney(value.toFixed(2))
            }
          />
          {/* Crossing zero is the event this whole project exists to surface. */}
          <ReferenceLine y={0} stroke="var(--text-secondary)" strokeDasharray="4 4" />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active !== true || payload === undefined || payload.length === 0) return null;
              const index = data.findIndex((point) => point["month"] === label);
              return (
                <div className="tooltip">
                  <strong>{String(label)}</strong>
                  {[...payload]
                    .sort((a, b) => Number(b.value) - Number(a.value))
                    .map((entry) => {
                      const rowId = String(entry.dataKey);
                      const row = plottable.find((candidate) => candidate.rowId === rowId);
                      const exact = labels.get(`${rowId}@${index}`);
                      return (
                        <div key={rowId}>
                          <span className="swatch" style={{ background: entry.color }} />
                          <span className="mono">{row?.jobCode}</span>
                          <span
                            className={
                              exact?.startsWith("-") === true ? "figure negative" : "figure"
                            }
                          >
                            {formatMoney(exact)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              );
            }}
          />
          {shown.map((row) => {
            const style = styles.get(row.rowId)!;
            return (
              <Line
                key={row.rowId}
                type="linear"
                dataKey={row.rowId}
                name={row.jobCode}
                stroke={style.colour}
                strokeWidth={2}
                {...(style.dash !== undefined ? { strokeDasharray: style.dash } : {})}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--surface-raised)" }}
                isAnimationActive={false}
                connectNulls
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      {/* A legend is always present for two or more series, so identity is
          never carried by colour alone — the job code is right there. */}
      <div className="legend">
        {plottable.map((row) => {
          const style = styles.get(row.rowId)!;
          const on = selected.has(row.rowId);
          return (
            <button
              key={row.rowId}
              className={on ? "legend-item on" : "legend-item"}
              aria-pressed={on}
              title={`${row.recipient} — ${formatMoney(row.overallPosition)}`}
              onClick={() =>
                setSelected((current) => {
                  const next = new Set(current);
                  if (next.has(row.rowId)) next.delete(row.rowId);
                  else next.add(row.rowId);
                  return next;
                })
              }
            >
              <span
                className="swatch-line"
                style={{
                  // The swatch shows the line as drawn, dash and all, so the
                  // legend and the chart cannot disagree about identity.
                  backgroundImage: on
                    ? `repeating-linear-gradient(to right, ${style.colour} 0 ${
                        style.dash === undefined ? "14px" : style.dash === "7 4" ? "5px" : "2px"
                      }, transparent ${
                        style.dash === undefined ? "14px" : style.dash === "7 4" ? "5px" : "2px"
                      } ${style.dash === undefined ? "14px" : style.dash === "7 4" ? "8px" : "4px"})`
                    : "none",
                }}
              />
              <span className="mono">{row.jobCode}</span>
              {!row.isStaff && <span className="tag">no target</span>}
            </button>
          );
        })}
      </div>

      <div className="legend-actions">
        <button onClick={() => setSelected(new Set(plottable.map((row) => row.rowId)))}>
          Select all
        </button>
        <button
          onClick={() =>
            setSelected(new Set(plottable.filter((row) => row.isStaff).map((row) => row.rowId)))
          }
        >
          Staff only
        </button>
        <button onClick={() => setSelected(new Set())}>Clear</button>
        <span className="muted">
          {shown.length} of {plottable.length} shown
          {plottable.length > SERIES_LIGHT.length &&
            " · past the eighth line, hue repeats with a dash pattern rather than a new colour"}
        </span>
      </div>
    </div>
  );
}
